import { useState } from "react";

import { ACTIONS, AREAS, divergence, enforcedMin, type Action } from "../../auth/actions";
import { AUTHORITY_MEANING } from "../../auth/authorities";
import { GROUP_LEVEL, LEVELS, LEVEL_NAME, levelOf, type Level } from "../../auth/levels";
import { useAccess } from "../../auth/useAccess";
import { Blurred } from "../../components/Blurred";
import { Badge, Card, Count, Empty, FilterBar, Notice, Page, Panel, SearchField, SelectField, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { ProductTag } from "../../products/PerProduct";
import { Health } from "./Health";

/**
 * Who may do what — the design's action map, with the levels derived.
 *
 * The design lists ACTIONS against a minimum level, which is how an operator
 * thinks: "can I set a tier?" This console gates on AUTHORITIES from the
 * `cognito:groups` claim, which is what the API gateway is to check — one
 * check for every product, rather than one per backend. The table below is
 * the design's, and every level in it comes from the authority map, so the
 * rung shown can never drift from what the console will allow.
 *
 * THE GATEWAY CHECK IS NOT LIVE YET, which the page states above the table.
 * Until it ships this map is the only gating, so the wording has to stop short
 * of implying a server-side refusal that cannot happen.
 *
 * IT ALSO REPORTS WHERE THE TWO DISAGREE. The design puts "Set tier for a
 * tenant" at L2; the deployed pool gives `tenant:write` to admin-operator,
 * which is L3. An operator refused a write the map promised them needs to see
 * that gap rather than conclude the console is broken.
 *
 * What this page must never do is imply a control we cannot enforce. "Dual
 * control" is rendered as what the DESIGN asks for, never as something that
 * happens: the admin API has no approver concept, and a static SPA holding a
 * bearer token cannot require two people — anyone who could complete a
 * ceremony here can call the same endpoint with curl.
 */
export function Permissions({ config }: { config: AppConfig }) {
  const access = useAccess();
  const mine = levelOf(access);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("all");

  const term = q.trim().toLowerCase();
  const rows = ACTIONS.filter(
    (a) =>
      (area === "all" || a.area === area) &&
      (!term || `${a.action} ${a.area} ${a.scope} ${a.story}`.toLowerCase().includes(term)),
  );

  return (
    <Page title="Permission map" subtitle="What each action takes, and what you hold.">

      {/* The ladder. Each rung IS a Cognito group — the four are strictly
          nested, which levels.ts asserts in a test rather than assuming — so
          the level is a name for a group and not a second model. L0 is what
          having no group looks like, which is why it names none. */}
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        {LEVELS.map((l) => (
          <LevelCard key={l} level={l} active={l === mine} />
        ))}
      </div>

      {(() => {
        const looser = ACTIONS.filter((a) => divergence(a) === "looser");
        if (looser.length === 0) return null;
        return (
          <Notice
            tone="amber"
            title={`${looser.length} action${looser.length === 1 ? " is" : "s are"} allowed at a lower level than the design reserves`}
          >
            <p>
              Nobody is refused, so nothing looks wrong — which is why this is stated rather than
              left to the rows. The API grants these through an authority that is broader than the
              design's split.
            </p>
            <ul className="mt-1.5 space-y-0.5 text-xs">
              {looser.map((a) => (
                <li key={a.action}>
                  <span className="font-medium">{a.action}</span> — reserved for{" "}
                  <span className="ref">{a.designMin}</span>, allowed here at{" "}
                  <span className="ref">{enforcedMin(a)}</span> via{" "}
                  <span className="ref">{a.needs}</span>
                </li>
              ))}
            </ul>
          </Notice>
        );
      })()}

      {/* WHAT IS TRUE TODAY, not the end state. This said "the server checks
          again" and "its answer wins", which reads as a live security
          boundary — and next to a greyed-out control that is exactly how it
          reads. Authorisation belongs at the API gateway by design, one check
          covering every product rather than one per backend; that check has
          not shipped, so until it does the console's own gating is the only
          gating. Restore the stronger wording when it lands, not before. */}
      <Notice tone="amber" title="The console checks this map. The gateway does not enforce it yet">
        <p>
          Every control on every page asks this same table whether to be live, so what
          you read here is what the console will let you do. Authorisation itself belongs
          at the API gateway — one place to change a role for every product — and{" "}
          <strong>that check is not live yet</strong>. Until it ships, a greyed-out
          control is this console withholding an action, not the API refusing it: the
          same request made directly would not be stopped.
        </p>
        <p className="mt-2">
          Once it does ship the server's answer wins, and a disagreement between the two
          means your token predates a group change — sign out and back in.
        </p>
      </Notice>

      <FilterBar>
        <SearchField value={q} onChange={setQ} placeholder="Search actions…" label="Search actions" />
        <SelectField value={area} onChange={setArea} label="Filter by area" width={180}>
          <option value="all">All areas</option>
          {AREAS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </SelectField>
        <Count shown={rows.length} total={ACTIONS.length} />
      </FilterBar>

      {/* The empty state sits BESIDE the table, not in a colSpan cell inside
          it. In the cell it inherited the row borders and the stripe, so "no
          actions match" arrived looking like a row of data. */}
      {rows.length === 0 ? (
        <Empty>No actions match.</Empty>
      ) : (
        <Table
          head={
            <tr>
              <Th>Area</Th>
              <Th>Action</Th>
              <Th>Scope</Th>
              <Th>Min level</Th>
              <Th>Dual control</Th>
              <Th>You</Th>
            </tr>
          }
        >
          {rows.map((a) => (
            <ActionRow key={`${a.area}-${a.action}`} action={a} mine={mine} />
          ))}
        </Table>
      )}

      <Card
        title="Grant or revoke a level"
        aside={<ProductTag id="mcp" />}
        note="Membership lives in Cognito, and this gateway exposes no admin route for it."
      >
        {/* The design's panel. The console can show what a group grants and
            not change who is in one. The working path stays what the sidebar
            describes: an admin-owner runs admin-add-user-to-group, then the
            operator signs out and back in. */}
        <Blurred
          what="Move an operator between groups"
          needs="a Cognito admin route on this gateway"
          minHeight={150}
        >
          <Panel className="p-4">
            <div className="flex flex-col gap-3">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="h-3 w-48 rounded bg-slate-950/10 dark:bg-white/10" />
                  <div className="h-7 w-28 rounded-lg bg-slate-950/5 ring-1 ring-slate-950/10 dark:bg-white/5 dark:ring-white/10" />
                </div>
              ))}
            </div>
          </Panel>
        </Blurred>
      </Card>

      <Card title="What this console cannot express">
        <div className="text-sm">
          <p className="font-medium">A two-person rule</p>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Eight actions above are marked <span className="ref">two people</span> because the
            design asks for it. <strong>Nothing here enforces that.</strong> The admin API has no
            approver concept, and this console is a static page holding your token — anyone who
            could complete an approval ceremony here can call the same endpoint directly. A
            second-approver field would imply a control that does not exist, and record a
            colleague&rsquo;s name against a change they never saw.
          </p>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            The nearest real thing: capping a <span className="ref">search_*</span> tool is
            refused by the server with the list of reports it would strand, and overriding that
            needs <span className="ref">quota:force</span> — which only{" "}
            <span className="ref">admin-owner</span> holds. That is a second, more privileged
            human, enforced where it counts.
          </p>
        </div>
      </Card>

      {/* Which product a token can even REACH is the other half of "who may
          write what": an authority means nothing against a gateway with no
          route for that product, and the map above is a claim about every
          product while only one is known to enforce it. */}
      <Health config={config} embedded />
    </Page>
  );
}

/** One rung. Names the group it is, because that is what the token carries. */
function LevelCard({ level, active }: { level: Level; active: boolean }) {
  const group = GROUP_FOR[level];
  return (
    <div
      className={`rounded-lg px-3 py-2 ring-1 ${
        active
          ? "bg-brand-weak ring-brand-500/30 dark:bg-brand-500/10 dark:ring-brand-300/30"
          : "ring-slate-950/10 dark:ring-white/10"
      }`}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-xs text-slate-500 dark:text-slate-400">{level}</span>
        <span className="truncate text-sm/5 font-medium text-slate-950 dark:text-white">
          {LEVEL_NAME[level]}
        </span>
      </div>
      {/* The design shows a rollout phase here. This shows the group, which is
          the thing an admin-owner actually adds someone to — and the only part
          of the rung this console can verify. */}
      <p
        className={`truncate font-mono text-[10px] ${
          group ? "text-slate-400 dark:text-slate-500" : "text-amber-700 dark:text-amber-400"
        }`}
        title={group ?? "No group. A signed-in user with none can read nothing and write nothing."}
      >
        {group ?? "no group"}
      </p>
    </div>
  );
}

/** level -> the group that is it. Inverted once, from the same table. */
const GROUP_FOR: Record<Level, string | null> = (() => {
  const out: Record<Level, string | null> = { L0: null, L1: null, L2: null, L3: null, L4: null };
  for (const [group, level] of Object.entries(GROUP_LEVEL)) out[level] = group;
  return out;
})();

function ActionRow({ action: a, mine }: { action: Action; mine: Level }) {
  const here = enforcedMin(a);
  const held = here !== null && LEVELS.indexOf(mine) >= LEVELS.indexOf(here);

  return (
    <tr>
      <Td>
        <span className="whitespace-nowrap text-slate-500 dark:text-slate-400">{a.area}</span>
      </Td>
      {/* WRAPS. F7's table cells inherit `whitespace-nowrap` from its scroll
          container, so the authority sentence and the `why` line held this
          column open to their full single-line width — pushing the table past
          1440px and scrolling the "You" column, the one an operator opens this
          page to read, off the right-hand edge. */}
      <Td>
        <div className="max-w-md whitespace-normal">
          <span className="font-medium">{a.action}</span>
          <div className="font-mono text-[10px] text-slate-400 dark:text-slate-500">{a.story}</div>
          {a.needs && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              <span className="ref">{a.needs}</span> — {AUTHORITY_MEANING[a.needs]}
            </div>
          )}
          {a.why && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400">{a.why}</div>
          )}
        </div>
      </Td>
      <Td>
        <span className="text-slate-500 dark:text-slate-400">{a.scope}</span>
      </Td>
      <Td>
        {a.blocked ? (
          <Badge tone="red">no level</Badge>
        ) : here === null ? (
          <span className="whitespace-nowrap">
            {a.product === "platform" ? (
              <Badge tone="neutral">not routable</Badge>
            ) : (
              <ProductTag id={a.product} />
            )}
          </span>
        ) : (
          <>
            <span className="font-mono text-sm">{here}</span>
            {/* The design's rung and the deployed pool's, when they differ —
                and WHICH WAY, because the two mean opposite things. Stricter
                is an operator refused a write the map promised them: visible
                and safe. Looser is someone quietly able to do a thing the
                design reserved for a more senior person, and nothing else in
                this console would ever surface it. */}
            {divergence(a) === "stricter" && (
              <div
                className="text-[11px] text-slate-500 dark:text-slate-400"
                title="The design places this lower than the deployed groups enforce, so an operator at the design's rung is refused."
              >
                design says {a.designMin}
              </div>
            )}
            {divergence(a) === "looser" && (
              <div
                className="text-[11px] font-medium text-amber-700 dark:text-amber-400"
                title="The deployed groups allow this at a LOWER rung than the design reserves it for. Nobody is refused, so nothing looks wrong."
              >
                design reserves this for {a.designMin}
              </div>
            )}
          </>
        )}
      </Td>
      <Td>
        {a.dual ? (
          <span title="What the design asks for. Nothing here enforces it — see below.">
            <Badge tone="amber">two people</Badge>
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </Td>
      <Td>
        {a.blocked ? (
          <Badge tone="red">not expressible</Badge>
        ) : here === null ? (
          <Badge tone="neutral">not built</Badge>
        ) : held ? (
          <Badge tone="green">allowed</Badge>
        ) : (
          <span title={`Needs ${here} ${LEVEL_NAME[here]}`}>
            <Badge tone="neutral">blocked</Badge>
          </span>
        )}
      </Td>
    </tr>
  );
}
